import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #viewportController;
  #details;

  #closeViewport = (restoreFocus = false) => {
    if (!this.#details?.open) return;
    this.#details.open = false;
    this.#syncViewport();
    if (restoreFocus) this.#details.querySelector('summary')?.focus({ preventScroll: true });
  };

  #syncViewport = () => {
    const summary = this.#details?.querySelector('summary');
    summary?.setAttribute('aria-expanded', String(this.#details.open));
    if (!this.#details.open) {
      if (this.matches(':popover-open')) this.hidePopover();
      return;
    }
    for (const panel of document.querySelectorAll('floating-panel-component[data-viewport-panel]:popover-open')) {
      if (panel === this) continue;
      panel.closest('details').open = false;
      panel.hidePopover();
    }
    // A manual popover escapes the scrolling bar without moving controls out of their form.
    if (!this.matches(':popover-open')) this.showPopover();
    this.#positionViewport(summary);
  };

  #positionViewport(summary) {
    const gutter = 12;
    const gap = 8;
    const anchor = summary.getBoundingClientRect();
    const viewport = window.visualViewport;
    const width = viewport?.width ?? window.innerWidth;
    const height = viewport?.height ?? window.innerHeight;
    const left = viewport?.offsetLeft ?? 0;
    const top = viewport?.offsetTop ?? 0;
    this.style.maxWidth = `${width - gutter * 2}px`;
    const below = top + height - anchor.bottom - gap - gutter;
    const above = anchor.top - top - gap - gutter;
    const naturalHeight = this.scrollHeight;
    const openAbove = naturalHeight > below && above > below;
    this.style.maxHeight = `${Math.max(0, openAbove ? above : below)}px`;
    const panel = this.getBoundingClientRect();
    this.style.left = `${Math.max(left + gutter, Math.min(anchor.left, left + width - panel.width - gutter))}px`;
    this.style.top = `${openAbove ? anchor.top - panel.height - gap : anchor.bottom + gap}px`;
  }

  #connectViewport() {
    this.#details = this.closest('details');
    if (!this.#details) return;
    this.#viewportController = new AbortController();
    const { signal } = this.#viewportController;
    this.#details.addEventListener('toggle', this.#syncViewport, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.#details.contains(event.target)) this.#closeViewport();
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#details.open) {
        event.preventDefault();
        this.#closeViewport(true);
      }
    }, { signal });
    document.addEventListener('focusin', (event) => {
      if (!this.#details.contains(event.target)) this.#closeViewport();
    }, { signal });
    window.addEventListener('scroll', (event) => {
      // Scrolling values inside the panel must remain possible.
      if (!this.contains(event.target)) this.#closeViewport(this.contains(document.activeElement));
    }, { capture: true, signal });
    window.addEventListener('resize', () => this.#closeViewport(this.contains(document.activeElement)), { signal });
    window.visualViewport?.addEventListener('resize', () => this.#closeViewport(this.contains(document.activeElement)), { signal });
    this.#syncViewport();
  }

  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute('data-viewport-panel')) {
      this.#connectViewport();
      return;
    }
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#viewportController?.abort();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
